/**
 * 基本信息 Tab
 * 头像上传、名称和描述编辑
 */

import { useState, useCallback, useRef } from 'react'
import { Camera, Loader2, Save, X } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { Label } from '@/shared/components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import { useToast } from '@/shared/hooks/useToast'
import { IdentityApiService, uploadAvatar } from '@/modules/identity/services'
import type { Identity } from '@/modules/identity/types'

interface BasicInfoTabProps {
  identity: Identity
  onUpdate: () => void
}

export function BasicInfoTab({ identity, onUpdate }: BasicInfoTabProps) {
  const { showSuccess, showError } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 表单状态
  const [name, setName] = useState(identity.name)
  const [description, setDescription] = useState(identity.description || '')
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  
  // 加载状态
  const [saving, setSaving] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)

  // 是否有修改
  const hasChanges = name !== identity.name || 
    description !== (identity.description || '') ||
    avatarFile !== null

  // 头像选择
  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      showError('请选择图片文件', '文件类型错误')
      return
    }

    // 验证文件大小（最大 5MB）
    if (file.size > 5 * 1024 * 1024) {
      showError('图片大小不能超过 5MB', '文件过大')
      return
    }

    setAvatarFile(file)
    // 创建预览
    const reader = new FileReader()
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleCancelAvatar = () => {
    setAvatarFile(null)
    setAvatarPreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // 保存
  const handleSave = useCallback(async () => {
    if (!name.trim()) {
      showError('名称不能为空', '验证失败')
      return
    }

    setSaving(true)
    try {
      // 如果有新头像，先上传
      let newAvatarUrl: string | undefined
      if (avatarFile) {
        setUploadingAvatar(true)
        try {
          newAvatarUrl = await uploadAvatar(identity.id, avatarFile)
        } catch (error) {
          console.error('Failed to upload avatar:', error)
          showError('头像上传失败', '上传失败')
          return
        } finally {
          setUploadingAvatar(false)
        }
      }

      // 更新基本信息
      await IdentityApiService.update(identity.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        avatar: newAvatarUrl,
      })

      showSuccess('基本信息已保存', '保存成功')
      
      // 清除头像预览
      setAvatarFile(null)
      setAvatarPreview(null)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      
      onUpdate()
    } catch (error) {
      console.error('Failed to save:', error)
      showError('保存失败，请稍后重试', '保存失败')
    } finally {
      setSaving(false)
    }
  }, [identity.id, name, description, avatarFile, showSuccess, showError, onUpdate])

  // 重置
  const handleReset = () => {
    setName(identity.name)
    setDescription(identity.description || '')
    handleCancelAvatar()
  }

  const avatarFallback = name.slice(0, 2).toUpperCase()

  return (
    <Card>
      <CardHeader>
        <CardTitle>基本信息</CardTitle>
        <CardDescription>
          编辑身份的基本信息，包括头像、名称和描述
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 头像上传 */}
        <div className="flex items-start gap-6">
          <div className="relative group">
            <Avatar className="h-24 w-24 rounded-xl">
              <AvatarImage 
                src={avatarPreview || identity.avatar} 
                alt={name}
                className="object-cover"
              />
              <AvatarFallback className="rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 text-violet-700 text-2xl font-semibold">
                {avatarFallback}
              </AvatarFallback>
            </Avatar>
            <button
              type="button"
              onClick={handleAvatarClick}
              className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
              disabled={uploadingAvatar}
            >
              {uploadingAvatar ? (
                <Loader2 className="h-6 w-6 text-white animate-spin" />
              ) : (
                <Camera className="h-6 w-6 text-white" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>
          <div className="flex-1 space-y-2">
            <p className="text-sm text-slate-600">点击头像更换图片</p>
            <p className="text-xs text-slate-400">
              支持 JPG、PNG、GIF 格式，最大 5MB
            </p>
            {avatarPreview && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancelAvatar}
                className="text-red-500 hover:text-red-600"
              >
                <X className="h-3 w-3 mr-1" />
                取消更换
              </Button>
            )}
          </div>
        </div>

        {/* 名称 */}
        <div className="space-y-2">
          <Label htmlFor="name">名称 *</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="输入身份名称"
            maxLength={50}
          />
          <p className="text-xs text-slate-400">
            {name.length}/50
          </p>
        </div>

        {/* 描述 */}
        <div className="space-y-2">
          <Label htmlFor="description">描述</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="描述这个身份的特点..."
            rows={4}
            maxLength={500}
          />
          <p className="text-xs text-slate-400">
            {description.length}/500
          </p>
        </div>

        {/* 元信息（只读） */}
        <div className="pt-4 border-t space-y-3">
          <h4 className="text-sm font-medium text-slate-700">其他信息</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">ID：</span>
              <span className="text-slate-700 font-mono text-xs ml-2">{identity.id}</span>
            </div>
            <div>
              <span className="text-slate-500">版本：</span>
              <span className="text-slate-700 ml-2">v{identity.version}</span>
            </div>
            <div>
              <span className="text-slate-500">创建时间：</span>
              <span className="text-slate-700 ml-2">
                {new Date(identity.created_at).toLocaleString('zh-CN')}
              </span>
            </div>
            <div>
              <span className="text-slate-500">更新时间：</span>
              <span className="text-slate-700 ml-2">
                {new Date(identity.updated_at).toLocaleString('zh-CN')}
              </span>
            </div>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleReset}
            disabled={!hasChanges || saving}
          >
            重置
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            保存
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
